import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { Code2, Copy, Edit3, History, WandSparkles } from 'lucide-react';
import {
  DashboardDetailsPageSkeleton,
  DashboardDetailsTab,
  DashboardDetailsTabList,
  DashboardDetailsHeader,
  dashboardPageTitleClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, DropdownMenuItem, Tabs } from '@shigo-ui/ui';

import { readShigoAppConfig } from '../../../../../lib/config';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../auth/useNetworkFeatures';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { usePublisherQuery } from '../../hooks';
import { createPublisherImmuTag } from '../../immuTag';
import { formatPublisherCost, PublisherDetailsTab } from '../../model';
import { resolveAvailablePublisherDetailsTabs } from '../../publisherDetailsAccess';
import { PublisherImmuTagDialog } from '../PublisherImmuTagDialog';
import { formatDisplayDate } from '../../../../../lib/format';
import { copyToClipboard } from '../../../../../lib/clipboard';

const tabDefinitions = [
  {
    value: PublisherDetailsTab.Overview,
    label: 'publisherDetails.tabs.overview',
    to: '/supply/publishers/$publisherId/details/overview',
  },
  {
    value: PublisherDetailsTab.Apps,
    label: 'publisherDetails.tabs.apps',
    to: '/supply/publishers/$publisherId/details/apps',
  },
  {
    value: PublisherDetailsTab.Domains,
    label: 'publisherDetails.tabs.domains',
    to: '/supply/publishers/$publisherId/details/domains',
  },
  {
    value: PublisherDetailsTab.Placements,
    label: 'publisherDetails.tabs.placements',
    to: '/supply/publishers/$publisherId/details/placements',
  },
  {
    value: PublisherDetailsTab.AppRequests,
    label: 'publisherDetails.tabs.appRequests',
    to: '/supply/publishers/$publisherId/details/app-requests',
  },
  {
    value: PublisherDetailsTab.A2v,
    label: 'publisherDetails.tabs.a2v',
    to: '/supply/publishers/$publisherId/details/a2v',
  },
] as const;

export function PublisherDetailsPage({
  publisherId,
  children,
}: {
  publisherId: string;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { permissions, can } = useShigoPermissions();
  const { features, isFeatureAllowed } = useNetworkFeatures();
  const config = readShigoAppConfig();
  const query = usePublisherQuery(publisherId);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [immuTagOpen, setImmuTagOpen] = useState(false);
  const availableTabs = resolveAvailablePublisherDetailsTabs(permissions, features);
  const tabs = tabDefinitions.filter((tab) => availableTabs.includes(tab.value));
  const activeTab = availableTabs.find((tab) => pathname.endsWith(`/${tab}`)) ?? availableTabs[0];
  const canEdit = can(ShigoPermission.SupplyPublishersEdit);
  const canReadChangelog = can(ShigoPermission.SupplyPublishersChangelogRead);
  const canGenerateEndpoint = can(ShigoPermission.SupplyPublishersEndpointGeneratorGenerate);
  const canUseImmu = isFeatureAllowed(NetworkAvailableFeature.Immu);

  if (query.isLoading) {
    return (
      <DashboardDetailsPageSkeleton
        label={dashboardT('publisherDetails.loading')}
        showHeaderAction={canEdit}
        metadataItemCount={4}
        showSecondaryAction={canReadChangelog}
        tabCount={tabs.length}
      />
    );
  }

  const publisher = query.data;

  async function copyAdsTxt() {
    if (!publisher?.secondaryId) return;
    const line = `${config.defaultAdsTxtDomain}, ${publisher.secondaryId}, DIRECT, ${config.defaultAdsTxtTagID}`;
    await copyToClipboard(line, {
      successMessage: dashboardT('publisherDetails.adsTxtCopied'),
      errorMessage: dashboardT('publisherDetails.adsTxtCopyError'),
    });
  }

  return (
    <>
      <main className="min-w-0 overflow-x-clip">
        <Tabs value={activeTab ?? PublisherDetailsTab.Overview}>
          <div className="border-border bg-card/60 border-b">
            <section className="page-gutter mx-auto flex w-full max-w-[1920px] flex-col gap-4 pt-5">
              <DashboardDetailsHeader
                back={
                  <ReturnBackLink
                    fallback={{ to: '/supply/publishers' }}
                    label={dashboardT('publishers.back')}
                  />
                }
                title={
                  <h1 className={dashboardPageTitleClassName('min-w-0 truncate')}>
                    {publisher?.name ?? dashboardT('publisherDetails.loadError')}
                  </h1>
                }
                metadata={
                  publisher
                    ? [
                        {
                          label: dashboardT('publishers.secondaryId'),
                          value: publisher.secondaryId ?? '-',
                        },
                        {
                          label: dashboardT('publishers.revenueType'),
                          value: formatPublisherCost(publisher.allProductsCost),
                        },
                        {
                          label: dashboardT('publishers.created'),
                          value: formatDisplayDate(publisher.createdOn),
                        },
                        {
                          label: dashboardT('publishers.updated'),
                          value: formatDisplayDate(publisher.updatedOn),
                        },
                      ]
                    : undefined
                }
                primaryAction={
                  canEdit && publisher ? (
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to="/supply/publishers/edit/$publisherId"
                        params={{ publisherId }}
                        search={{}}
                      >
                        <Edit3 className="size-4" aria-hidden="true" />
                        {dashboardT('publishers.edit')}
                      </Link>
                    </Button>
                  ) : undefined
                }
                moreActionsLabel={dashboardT('publisherDetails.moreActions')}
                moreActions={
                  publisher &&
                  (canUseImmu ||
                    publisher.secondaryId ||
                    canGenerateEndpoint ||
                    canReadChangelog) ? (
                    <>
                      {canUseImmu ? (
                        <DropdownMenuItem
                          onSelect={() => {
                            setImmuTagOpen(true);
                          }}
                        >
                          <Code2 className="size-4" aria-hidden="true" />
                          {dashboardT('publisherDetails.immuTag')}
                        </DropdownMenuItem>
                      ) : null}
                      {publisher.secondaryId ? (
                        <DropdownMenuItem onSelect={() => void copyAdsTxt()}>
                          <Copy className="size-4" aria-hidden="true" />
                          {dashboardT('publisherDetails.adsTxt')}
                        </DropdownMenuItem>
                      ) : null}
                      {canGenerateEndpoint ? (
                        <DropdownMenuItem asChild>
                          <Link to="/supply/endpoint-generator" search={{ publisherId }}>
                            <WandSparkles className="size-4" aria-hidden="true" />
                            {dashboardT('publisherDetails.endpointGenerator')}
                          </Link>
                        </DropdownMenuItem>
                      ) : null}
                      {canReadChangelog ? (
                        <DropdownMenuItem
                          onSelect={() => {
                            setChangelogOpen(true);
                          }}
                        >
                          <History className="size-4" aria-hidden="true" />
                          {dashboardT('publisherDetails.changelog')}
                        </DropdownMenuItem>
                      ) : null}
                    </>
                  ) : undefined
                }
              />
              <DashboardDetailsTabList aria-label={dashboardT('publisherDetails.tabsLabel')}>
                {tabs.map((tab) => {
                  return (
                    <DashboardDetailsTab key={tab.value} value={tab.value} asChild>
                      <Link to={tab.to} params={{ publisherId }} search={{}}>
                        {dashboardT(tab.label)}
                      </Link>
                    </DashboardDetailsTab>
                  );
                })}
              </DashboardDetailsTabList>
            </section>
          </div>
          <section className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 py-6">
            {query.isError ? (
              <div className="border-border bg-card space-y-4 rounded-md border p-6">
                <p>{dashboardT('publisherDetails.loadError')}</p>
                <Button type="button" variant="outline" onClick={() => void query.refetch()}>
                  {dashboardT('common.retry')}
                </Button>
              </div>
            ) : (
              children
            )}
          </section>
        </Tabs>
      </main>
      {publisher && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('publisherDetails.changelogTitle', { name: publisher.name })}
          description={dashboardT('publisherDetails.changelogDescription')}
          scope={{ publisherIds: [publisher.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
      {publisher && immuTagOpen ? (
        <PublisherImmuTagDialog
          tag={createPublisherImmuTag(publisher.secondaryId, config.networkId, config.cdnUrl)}
          onOpenChange={setImmuTagOpen}
        />
      ) : null}
    </>
  );
}
